/**
 * BLOOM 세션 보관.
 *
 * 토큰은 `{userId}.{issuedAtMs}.{HMAC}` 이고 서버가 서명한다 (`docs/13` §4.1 —
 * 세션 테이블도 역할 체계도 만들지 않는다). 앱이 하는 일은 그것을 들고 있다가
 * `Authorization: Bearer` 로 붙이는 것뿐이다.
 *
 * **관리자 세션 쿠키(`bloom_admin_session`)와 절대 섞지 않는다** (절대 규칙 15 의 이웃,
 * `docs/13` §3.3). 자격 셋이 한 서버에 공존하고, 섞이면 관리자 권한이 시청자 토큰으로
 * 열린다. 그래서 쿠키가 아니라 저장소이고 헤더로만 나간다.
 *
 * 저장소 접근을 전부 감싼다 — 사파리 프라이빗 모드는 `localStorage` 에서 **예외를
 * 던지고**, 토큰을 못 보관하는 것이 시청을 막을 이유는 없다 (`LocalStorageAdapter` 와
 * 같은 방침). 그때는 로그인이 새로고침마다 풀릴 뿐이다.
 */

import { RECEIVER_DEVICE_ID_KEY } from '@apm/bloom-receiver-sdk';

export const BLOOM_STORAGE_KEYS = {
  /** `docs/15` §1 의 하네스가 이 키로 토큰을 심는다. 바꾸면 검증 다섯이 함께 깨진다 */
  token: 'bloom.token',
  /** 로그인 뒤 되돌아갈 곳. QR 로 들어온 사람의 `?ref=` 가 여기 실려 살아남는다 */
  returnTo: 'bloom.returnTo',
  /**
   * 방금 로그인했는가. **`account_linked` 를 낼 수 있는 시점이 로그인 순간이 아니라서
   * 필요하다** — 로그인은 페이지를 다시 열고, SDK 는 그 뒤에 서니 이벤트를 낼 통로가
   * 그 순간에는 없다 (`docs/13` §8).
   */
  justLoggedIn: 'bloom.justLoggedIn',
  /**
   * GIS redirect 콜백이 실패로 돌아왔는가 (v17 R9).
   *
   * **서버가 JSON 오류를 그리지 않기 때문에 필요하다** — 구글이 보낸 POST 의 응답을 보고
   * 있는 것은 사람이라, 서버는 앱으로 되돌리고 **화면이 대신 말한다.** 그 사이에 문서가
   * 한 번 갈리므로 상태로는 넘길 수 없다 (`justLoggedIn` 과 같은 처지).
   */
  loginError: 'bloom.loginError',
} as const;

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* 프라이빗 모드. 로그인이 새로고침마다 풀릴 뿐 시청은 그대로다 */
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* 위와 같다 */
  }
}

/** v17 R9. 콜백이 남긴 실패 코드를 **한 번만** 준다 */
export function writeLoginError(code: string): void {
  write(BLOOM_STORAGE_KEYS.loginError, code);
}

export function takeLoginError(): string | null {
  const value = read(BLOOM_STORAGE_KEYS.loginError);
  if (value !== null) remove(BLOOM_STORAGE_KEYS.loginError);
  return value;
}

/**
 * 시청 이력의 **기기 축** 식별자. 없으면 `null` (v17 R1 · R12 가 고쳤다).
 *
 * ── ★ 이 앱에는 기기 ID 가 **둘**이고 서로 모른다 ──────────────
 *
 * | 저장소 키 | 임자 | 무엇의 축인가 |
 * |---|---|---|
 * | `fast.deviceId` | **Receiver SDK** | 플레이리스트의 `?deviceId=` · **`WatchProgress` 의 기기 축** |
 * | `tv2m.deviceId` | TV2M SDK | `BloomDevice` · 가구 그래프 · **발송 대상** |
 *
 * **두 값은 다르고 어느 쪽도 상대를 알지 못한다.** 서버의 `promoteProgress` 는
 * `WatchProgress.scopeId` 로 찾으므로 **앞엣것**이어야 한다 — 뒤엣것을 실어 보내면 찾을
 * 행이 없어 **승격이 조용히 무동작**이 된다. 로그인은 성공하고 이력만 올라오지 않아,
 * 증상은 **「로그인했더니 이어보기가 처음으로 돌아갔다」** 하나다.
 *
 * **v17 이 그 실수를 한 번 저질렀다** (2026-08-18). R1 이 `tv2m.deviceId` 를 읽었고,
 * 실기에서 **「로그아웃 상태로 본 회차가 로그인 뒤에 올라오지 않는다」**로 드러났다.
 * 단위 테스트는 본문 조립만 봤기 때문에 **초록인 채였다** — `docs/31` §7.2-e.
 *
 * ── 저장소에서 읽는다. SDK 인스턴스에서 읽지 않는다 ──────────
 *
 * 로그인 화면은 `App.tsx` 의 게이트 안이라 `useReceiver` 도 `useTv2m` 도 아직 마운트되지
 * 않았다. **키의 임자는 Receiver SDK 이므로 사본을 두지 않고 그 상수를 그대로 읽는다.**
 *
 * **이 값이 없는 것은 정상일 수 있다** — 아직 한 번도 재생하지 않은 기기가 그렇고,
 * 그때는 승격할 기기 축 진행도 없다.
 */
export function readViewerDeviceId(): string | null {
  return read(RECEIVER_DEVICE_ID_KEY);
}

export function readToken(): string | null {
  const token = read(BLOOM_STORAGE_KEYS.token);
  return token === null || token === '' ? null : token;
}

export function writeToken(token: string): void {
  write(BLOOM_STORAGE_KEYS.token, token);
}

export function clearToken(): void {
  remove(BLOOM_STORAGE_KEYS.token);
}

/**
 * 로그인 화면을 띄우기 **직전에** 지금 URL 을 통째로 보관한다.
 *
 * ── 왜 쿼리·해시까지 보관하는가 ───────────────────────────────
 *
 * QR 로 온 사람은 `/install?ref=tv2m_qr_{token}` 으로 들어오고 **아직 로그인 전이다.**
 * 그 `ref` 를 잃으면 서버가 지문으로 내려가지 않으므로(T5 의 명시적 규칙) 복원이 통째로
 * 실패하고, 증상은 「설치하고 돌아왔는데 상품이 없다」 하나다 (`docs/12` §8 ④).
 * Deep Link(`/tv2m/product?id=…&match_id=…`)도 같은 이유다.
 */
export function saveReturnTo(href: string, origin: string): void {
  write(BLOOM_STORAGE_KEYS.returnTo, sanitizeReturnTo(href, origin));
}

/** 읽고 지운다. 한 번 쓴 목적지를 남겨 두면 다음 로그인이 엉뚱한 곳으로 간다. */
export function takeReturnTo(): string | null {
  const value = read(BLOOM_STORAGE_KEYS.returnTo);
  remove(BLOOM_STORAGE_KEYS.returnTo);
  return value;
}

export function markJustLoggedIn(): void {
  write(BLOOM_STORAGE_KEYS.justLoggedIn, '1');
}

/**
 * 읽고 지운다. **한 번만 세야 한다** — 남겨 두면 새로고침마다 `account_linked` 가
 * 하나씩 더 쌓이고, 그 이벤트로 계정 연결을 세는 분석이 부풀려진다.
 */
export function takeJustLoggedIn(): boolean {
  const value = read(BLOOM_STORAGE_KEYS.justLoggedIn);
  remove(BLOOM_STORAGE_KEYS.justLoggedIn);
  return value === '1';
}

/**
 * 되돌아갈 곳을 **같은 오리진의 경로 하나로** 좁힌다.
 *
 * 둘을 막는다.
 *
 * 1. **다른 오리진.** 저장소에 든 값을 그대로 `location.replace` 에 넘기면 그것이 곧
 *    오픈 리다이렉트다 — 로그인 직후 남의 사이트로 튕겨 낼 수 있다.
 * 2. **`/login` 자신.** 로그인 화면에서 로그인하면 로그인 화면으로 돌아오게 된다.
 *
 * 파싱에 실패하거나 걸리면 `/` 다. 「어디로 돌아갈지 모르겠으면 방송으로」가 이 앱의
 * 기본값이다 (`routing.ts` 가 모르는 경로를 시청 화면으로 보내는 것과 같다).
 */
export function sanitizeReturnTo(href: string, origin: string): string {
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return '/';
  }
  if (url.origin !== origin) return '/';
  if (url.pathname.replace(/\/+$/, '') === '/login') return '/';
  return `${url.pathname}${url.search}${url.hash}`;
}
